import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileRenderContext, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContentRequests = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  get quickAddButton() {
    return this.dataset.quickAddButton || 'choose';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.syncButtonState();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('pointerdown', this.#handleQuickAddPointerDown);
    this.addEventListener('click', this.#stopQuickAddTriggerPropagation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerdown', this.#handleQuickAddPointerDown);
    this.removeEventListener('click', this.#stopQuickAddTriggerPropagation);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    const productGrid = await this.fetchProductPage(currentUrl);

    if (!productGrid) return;

    await this.updateQuickAddModal(productGrid);
    this.#openQuickAddModal();
  };

  #stopQuickAddTriggerPropagation = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.quick-add__button')) {
      event.stopPropagation();
    }
  };

  #handleQuickAddPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const quickAddButton = event.target.closest('.quick-add__button');
    if (!quickAddButton || !this.contains(quickAddButton)) return;

    event.stopPropagation();

    if (quickAddButton.classList.contains('quick-add__button--choose')) {
      this.#warmProductPage();
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  #warmProductPage() {
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    void this.fetchProductPage(currentUrl).catch(() => {});
  }

  syncButtonState() {
    const productCard = this.closest('product-card');
    const productFormComponent = this.querySelector('.quick-add__product-form-component');
    if (!(productCard instanceof HTMLElement) || !(productFormComponent instanceof HTMLElement)) return;

    let quickAddButton = 'choose';

    if (productFormComponent.classList.contains('quick-add__product-form-component--single-variant')) {
      quickAddButton = 'add';
    } else if (
      productFormComponent.classList.contains('quick-add__product-form-component--single-option') &&
      productCard.querySelector('swatches-variant-picker-component') &&
      !productCard.hasAttribute('data-no-swatch-selected')
    ) {
      quickAddButton = 'add';
    }

    this.dataset.quickAddButton = quickAddButton;
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Element | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cachedContent = this.#cloneCachedContent(productPageUrl);
    if (cachedContent) return cachedContent;

    const pendingRequest = this.#pendingContentRequests.get(productPageUrl);
    if (pendingRequest) {
      const pendingContent = await pendingRequest;
      return pendingContent ? /** @type {Element} */ (pendingContent.cloneNode(true)) : null;
    }

    const request = this.#fetchAndCacheProductGrid(productPageUrl);
    this.#pendingContentRequests.set(productPageUrl, request);

    try {
      const productGrid = await request;
      return productGrid ? /** @type {Element} */ (productGrid.cloneNode(true)) : null;
    } finally {
      this.#pendingContentRequests.delete(productPageUrl);
    }
  }

  /**
   * Returns a clone of any cached quick-add grid content for a given product URL.
   * @param {string} productPageUrl
   * @returns {Element | null}
   */
  #cloneCachedContent(productPageUrl) {
    const cachedContent = this.#cachedContent.get(productPageUrl);
    return cachedContent ? /** @type {Element} */ (cachedContent.cloneNode(true)) : null;
  }

  /**
   * Fetches and caches the product grid content used by quick add.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #fetchAndCacheProductGrid(productPageUrl) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');

      if (!gridElement) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(productPageUrl, productGrid);

      return productGrid;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileRenderContext()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
